import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../../services/api";
import { useSocket } from "../../../hooks/useSocket";
import { useUser } from "../../../context/UserContext";
import ListingCard from "./ListingCard";
import LobbyDetailModal from "./LobbyDetailModal";
import styles from "./BrowseListings.module.scss";
import type { BrowseListingView, BrowseListingsParams } from "../../../services/api/LfgApi";

interface BrowseListingsProps {
  filters: BrowseListingsParams;
  games?: Array<{ gameId: string; name: string }>;
  onJoin?: () => void;
}

const BrowseListings: React.FC<BrowseListingsProps> = ({ filters, onJoin }) => {
  const { subscribe, emit, connected } = useSocket();
  const { linkedAccounts, refreshLinkedAccounts } = useUser();

  const discordLinked = linkedAccounts.includes("discord");

  // Refresh linked accounts when tab becomes visible again,
  // so gating controls update after the user links/unlinks Discord in settings
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshLinkedAccounts();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshLinkedAccounts]);

  const [listings, setListings] = useState<BrowseListingView[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<BrowseListingView | null>(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchListings = useCallback(
    async (pageNum: number, append: boolean = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await api.lfg.browseListings({
          ...filters,
          page: pageNum,
        });

        if (append) {
          setListings((prev) => [...prev, ...response.listings]);
        } else {
          setListings(response.listings);
        }
        setHasMore(response.hasMore);
      } catch (err: any) {
        setError(err?.message || "Failed to load listings");
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [filters]
  );

  // Reset and re-fetch when filters change
  useEffect(() => {
    setListings([]);
    setPage(1);
    setHasMore(true);
    fetchListings(1, false);
  }, [fetchListings]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchListings(nextPage, true);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, page, fetchListings]);

  // Real-time browse updates — subscribe to LFG_UPDATED signal
  useEffect(() => {
    if (!connected) return;

    emit("subscribe_lfg_browse");

    if (filters.gameId) {
      emit("subscribe_lfg_game", { gameId: filters.gameId });
    }

    const unsub = subscribe("LFG_UPDATED", () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchListings(1, false);
      }, 300);
    });

    // Lightweight fallback poll every 30s in case socket signal was missed
    const pollInterval = setInterval(() => {
      fetchListings(1, false);
    }, 30000);

    return () => {
      emit("unsubscribe_lfg_browse");
      if (filters.gameId) {
        emit("unsubscribe_lfg_game", { gameId: filters.gameId });
      }
      unsub();
      clearInterval(pollInterval);
    };
  }, [filters.gameId, subscribe, emit, connected, fetchListings]);

  // Error state with retry
  if (error && listings.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "var(--space-lg, 24px)" }}>
        <p>{error}</p>
        <button
          onClick={() => {
            setPage(1);
            fetchListings(1, false);
          }}
          style={{
            marginTop: "var(--space-sm, 12px)",
            padding: "var(--space-xs, 8px) var(--space-md, 16px)",
            borderRadius: "6px",
            border: "none",
            background: "var(--color-primary, #6366f1)",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Initial loading
  if (loading && listings.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-xl, 48px)" }}>
        <div className={styles.spinner} />
      </div>
    );
  }

  // Empty state
  if (!loading && listings.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "var(--space-lg, 24px)", color: "var(--color-text-secondary, #a1a1aa)" }}>
        <p>No listings found. Try adjusting your filters or create a new listing.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Lobby</th>
            <th className={styles.th}>Game</th>
            <th className={styles.th}>Description</th>
            <th className={styles.th}>Host</th>
            <th className={styles.th}>Players</th>
            <th className={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {listings.map((listing) => (
            <ListingCard
              key={listing.lobbyId}
              listing={listing}
              discordLinked={discordLinked}
              onJoin={onJoin}
              onClick={() => setSelectedListing(listing)}
            />
          ))}
        </tbody>
      </table>

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-md, 16px)" }}>
          <div className={styles.spinner} />
        </div>
      )}

      {hasMore && !loading && (
        <div ref={sentinelRef} style={{ height: "1px" }} />
      )}

      {selectedListing && (
        <LobbyDetailModal
          listing={selectedListing}
          onClose={() => setSelectedListing(null)}
          onJoin={onJoin}
        />
      )}
    </div>
  );
};

export default BrowseListings;
